'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Navigation from '@/components/Navigation';
import { fetchAllPaddles, type Paddle } from '@/lib/services/paddleService';
import { useComparison } from '@/contexts/ComparisonContext';

export default function CompareBuilderPage() {
  const router = useRouter();
  const { selectedItems, addItem, removeItem } = useComparison();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Paddle[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [allPaddles, setAllPaddles] = useState<Paddle[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch all paddles from Supabase on mount
  useEffect(() => {
    fetchAllPaddles().then(data => setAllPaddles(data));
  }, []);

  const handleSearch = (q: string) => {
    setQuery(q);
    if (q.length > 0) {
      const filtered = allPaddles
        .filter(p =>
          !selectedItems.find(s => s.id === p.id) &&
          (p.name.toLowerCase().includes(q.toLowerCase()) ||
           p.brand.toLowerCase().includes(q.toLowerCase()))
        )
        .slice(0, 8);
      setResults(filtered);
      setShowDropdown(true);
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (paddle: Paddle) => {
    addItem(paddle);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleCompare = () => {
    if (selectedItems.length < 2) return;
    const ids = selectedItems.map(p => p.slug).join('-vs-');
    router.push(`/compare/${ids}`);
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: '#EBEBEB' }}
    >
      {/* Subtle red ambient glow top-right */}
      <div className="absolute pointer-events-none" style={{
        top: '-8%', right: '-4%', width: '600px', height: '600px',
        background: 'radial-gradient(ellipse, rgba(217,0,18,0.05) 0%, transparent 65%)',
        borderRadius: '50%',
      }} />

      <Navigation />

      {/* Centered builder */}
      <div className="flex-1 flex items-center justify-center px-4 py-28 relative z-10">
        <div className="w-full max-w-lg">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="premium-chip accent mx-auto mb-5" style={{ width: 'fit-content' }}>Compare</div>
            <h1
              className="font-black mb-3"
              style={{
                fontSize: 'clamp(28px, 4.5vw, 44px)',
                letterSpacing: '-0.048em',
                color: '#0A0A0A',
                lineHeight: 1.06,
              }}
            >
              Compare any two paddles
            </h1>
            <p style={{ color: 'rgba(10,10,10,0.42)', fontSize: '15px', lineHeight: 1.75 }}>
              Search and select two paddles to compare side-by-side
            </p>
          </div>

          {/* Search box */}
          <div ref={dropdownRef} className="relative mb-6">
            <div
              className="flex items-center rounded-2xl px-5 gap-3"
              style={{
                background: '#FFFFFF',
                border: '2px solid rgba(0,0,0,0.09)',
                height: 56,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="rgba(10,10,10,0.3)" style={{ flexShrink: 0 }}>
                <path d="M6.5 0a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 1a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zm4.78 9.72l3.5 3.5-.7.7-3.5-3.5.7-.7z"/>
              </svg>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search paddle name or brand…"
                value={query}
                onChange={e => handleSearch(e.target.value)}
                className="bg-transparent border-none outline-none text-sm w-full font-semibold"
                style={{ color: '#0A0A0A', fontFamily: 'inherit', letterSpacing: '-0.01em' }}
                onFocus={e => {
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.style.borderColor = 'rgba(217,0,18,0.5)';
                    parent.style.boxShadow = '0 0 0 4px rgba(217,0,18,0.1)';
                  }
                }}
                onBlur={e => {
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.style.borderColor = 'rgba(0,0,0,0.09)';
                    parent.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  }
                }}
              />
              {query && (
                <button
                  onClick={() => { setQuery(''); setResults([]); setShowDropdown(false); inputRef.current?.focus(); }}
                  className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.08)', color: 'rgba(10,10,10,0.5)' }}
                >
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M2 2l6 6M8 2l-6 6"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Dropdown results */}
            {showDropdown && results.length > 0 && (
              <div
                className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden"
                style={{
                  background: '#FFFFFF',
                  border: '1.5px solid rgba(0,0,0,0.08)',
                  borderRadius: '18px',
                  boxShadow: '0 28px 72px rgba(0,0,0,0.18), 0 4px 14px rgba(0,0,0,0.07)',
                }}
              >
                {results.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => handleSelect(p)}
                    className="w-full flex items-center gap-3.5 px-5 py-3.5 text-left transition-all"
                    style={{
                      borderBottom: i < results.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                      transitionDuration: '0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(217,0,18,0.03)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div
                      className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0"
                      style={{ background: 'linear-gradient(145deg, #F8F8F8, #EFEFEF)', border: '1px solid rgba(0,0,0,0.06)' }}
                    >
                      <img
                        src={p.image}
                        alt={p.imageAlt}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate" style={{ color: '#0A0A0A', letterSpacing: '-0.015em' }}>{p.name}</div>
                      <div className="text-xs mt-0.5 font-semibold" style={{ color: 'rgba(10,10,10,0.38)' }}>{p.brand} · {p.priceFormatted}</div>
                    </div>
                    <div className="text-sm font-black flex-shrink-0 tabular-nums" style={{ color: '#D90012' }}>{isNaN(p.score) || !isFinite(p.score) ? 70 : p.score}pts</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected paddles */}
          {selectedItems.length > 0 && (
            <div className="mb-6 space-y-3">
              {selectedItems.map((p, i) => (
                <div
                  key={p.id}
                  className="flex items-center gap-4 rounded-2xl px-5 py-4"
                  style={{
                    background: '#FFFFFF',
                    border: '1.5px solid rgba(217,0,18,0.16)',
                    boxShadow: '0 2px 8px rgba(217,0,18,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0"
                    style={{ background: 'linear-gradient(145deg, #F8F8F8, #EFEFEF)', border: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <img
                      src={p.image}
                      alt={p.imageAlt}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate" style={{ color: '#0A0A0A', letterSpacing: '-0.015em' }}>{p.name}</div>
                    <div className="text-xs mt-0.5 font-semibold" style={{ color: 'rgba(10,10,10,0.38)' }}>{p.brand} · {p.priceFormatted}</div>
                  </div>
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                    style={{ background: 'rgba(217,0,18,0.08)', color: '#D90012', border: '1.5px solid rgba(217,0,18,0.16)' }}
                  >
                    {i + 1}
                  </div>
                  <button
                    onClick={() => removeItem(p.id)}
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.05)', color: 'rgba(10,10,10,0.4)', transition: 'background 0.15s ease' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(217,0,18,0.08)'; (e.currentTarget as HTMLElement).style.color = '#D90012'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.05)'; (e.currentTarget as HTMLElement).style.color = 'rgba(10,10,10,0.4)'; }}
                  >
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M2 2l6 6M8 2l-6 6"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Compare button */}
          <button
            onClick={handleCompare}
            disabled={selectedItems.length < 2}
            className="w-full versusButton pill"
            style={{
              opacity: selectedItems.length < 2 ? 0.4 : 1,
              cursor: selectedItems.length < 2 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              padding: '14px 24px',
            }}
          >
            {selectedItems.length < 2
              ? `Select ${2 - selectedItems.length} more paddle${2 - selectedItems.length === 1 ? '' : 's'}`
              : 'Compare Now →'}
          </button>
        </div>
      </div>
    </div>
  );
}
